<script setup lang="ts" generic="T extends string | number">
import { computed, provide, ref, toRef, useId } from 'vue';
import { nxRadioGroupInjectionKey, type NxRadioItemRegistration } from './radio-internal';
import { firstEnabledIndex, lastEnabledIndex, nextEnabledIndex } from './radioNavigation';
import { useInputSaveStatus } from '../composables/useInputSaveStatus';

export type NxRadioGroupLayout = 'horizontal' | 'vertical' | 'grid';

const props = withDefaults(
  defineProps<{
    name?: string;
    /** 'horizontal', 'vertical' or 'grid' (= horizontal, each item same size) */
    layout?: NxRadioGroupLayout;
    disabled?: boolean;
    /** Defaults to var(--nxt-space-xs). Null means dense layout (shared border) */
    gap?: string | null;
    label?: string;
  }>(),
  {
    layout: 'horizontal',
    disabled: false,
    name: undefined,
    gap: 'var(--nxt-space-xs)',
    label: undefined,
  }
);

const model = defineModel<T>({ required: true });

// Stable base name generated once in setup (`useId()` returns a new id per call,
// so it must not live in a getter). Each item derives a UNIQUE input name from
// it, which switches OFF native radio grouping — we drive navigation and
// selection ourselves so arrow keys move focus without selecting.
const generatedName = `radio_group_${useId()}`;

// Items self-register (in DOM/mount order) so the group can run a roving
// tabindex and move focus between them on arrow keys.
const items = ref<NxRadioItemRegistration[]>([]);
// The item the roving tabindex currently points at (null until first navigation).
const focusedValue = ref<string | number | null>(null);

function register(registration: NxRadioItemRegistration): () => void {
  items.value.push(registration);
  return () => {
    const index = items.value.indexOf(registration);
    if (index !== -1) items.value.splice(index, 1);
  };
}

// The single item in the tab sequence: the one the user navigated to, else the
// checked item, else the first enabled item.
const activeValue = computed<string | number | null>(() => {
  const enabled = items.value.filter((item) => !item.disabled());
  if (focusedValue.value !== null && enabled.some((i) => i.value === focusedValue.value)) {
    return focusedValue.value;
  }
  if (enabled.some((i) => i.value === model.value)) return model.value;
  return enabled[0]?.value ?? null;
});

function select(value: string | number): void {
  if (props.disabled) return;
  const item = items.value.find((i) => i.value === value);
  if (!item || item.disabled()) return;
  model.value = value as T;
  focusedValue.value = value;
}

function focusIndex(index: number): void {
  const item = items.value[index];
  if (!item) return;
  focusedValue.value = item.value;
  item.focus();
}

// Arrow keys navigate (move focus only); Space/Enter select the focused item.
function onKeydown(event: KeyboardEvent): void {
  if (props.disabled) return;
  const disabledFlags = items.value.map((i) => i.disabled());
  const current = items.value.findIndex((i) => i.value === activeValue.value);

  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      focusIndex(nextEnabledIndex(disabledFlags, current, 1));
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      focusIndex(nextEnabledIndex(disabledFlags, current, -1));
      break;
    case 'Home':
      event.preventDefault();
      focusIndex(firstEnabledIndex(disabledFlags));
      break;
    case 'End':
      event.preventDefault();
      focusIndex(lastEnabledIndex(disabledFlags));
      break;
    case ' ':
    case 'Spacebar':
    case 'Enter':
      event.preventDefault();
      if (activeValue.value !== null) select(activeValue.value);
      break;
  }
}

// Save status (loading/success/error) from an ancestor NxLoadingWrapper.
// The group is one field: success/error shows a badge on the selected item
// (see items), and loading disables every item via the provided `disabled` below.
const {
  visualState: saveState,
  disabled: saveDisabled,
  errorMessage,
  fadeOut: saveFadeOut,
  srText,
  ariaBindings,
} = useInputSaveStatus();

provide(nxRadioGroupInjectionKey, {
  modelValue: model,
  name: toRef(() => props.name ?? generatedName),
  disabled: computed(() => props.disabled || saveDisabled.value),
  activeValue,
  select,
  register,
  saveState,
  errorMessage,
  saveFadeOut,
});
</script>

<template>
  <div
    class="nx-radio-group"
    :class="`layout-${props.layout}`"
    role="radiogroup"
    v-bind="ariaBindings"
    :style="`gap: ${props.gap}`"
    :data-dense-radio-group="props.gap === null ? true : false"
    @keydown="onKeydown"
  >
    <div v-if="props.label" class="nx-radio-label">{{ props.label }}</div>
    <slot />
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </div>
</template>

<style scoped>
.nx-radio-group {
  display: flex;
}

.nx-radio-group.layout-horizontal {
  flex-direction: row;
  flex-wrap: wrap;
}

.nx-radio-group.layout-vertical {
  flex-direction: column;
}

.nx-radio-group.layout-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
}

.nx-radio-label {
  font-size: var(--nxt-font-size-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--nxt-space-xs);
  text-transform: uppercase;
  color: var(--nxt-fg-muted);
  background-color: transparent;
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-radio-group[data-dense-radio-group='true'] .nx-radio-label {
  border-radius: var(--nxt-radius-md) 0 0 var(--nxt-radius-md);
  border: var(--nxt-hairline) solid var(--nxt-border);
  line-height: var(--nxt-line-height-control);
}
.nx-radio-group[data-dense-radio-group='true']:has(.icon-only) .nx-radio-label {
  height: calc(1lh + 2 * var(--nxt-space-xs));
}

/* --- I-422 save status: sr-only live region for the whole group --- */
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}
</style>
